import { defineComponent as _defineComponent } from 'vue'
import { useIsDesktop } from "@/features/useIsDesktop";
  import {
    builderDemoImagesData,
    landingFloorplansData,
  } from "@/constants/landingData";

  import {
    ref,
    computed,
    nextTick,
    onMounted,
    onBeforeUnmount,
    watch,
  } from "vue";
  import { scrollToElementIfNotVisible } from "@/helpers/scrollIfElementIsNotVisible";
  import { Steps } from "@/enums/BuilderDemoSteps";
  import BuilderDemoMenuSelection from "./BuilderDemoMenuSelection.vue";

  const IS_DESKTOP_BREAKPOINT = 680;
  const FIXED_HEADER_HEIGHT = 140;
  const LOT_INFO_ELEMENT_SELECTOR = "#lot-info-card";
  const DEFAULT_INTERVAL_DURATION = 6000;

  
export default /*@__PURE__*/_defineComponent({
  __name: 'BuilderDemo',
  setup(__props, { expose: __expose }) {
  __expose();

  const { isDesktop } = useIsDesktop(IS_DESKTOP_BREAKPOINT);
  const sliderSteps = ref([...Object.values(Steps)]);
  const currentStepIndex = ref(0);
  const currentStep = computed(() => sliderSteps.value[currentStepIndex.value]);
  const prevStep = computed(
    () => sliderSteps.value[currentStepIndex.value - 1]
  );
  const isFirstStep = computed(() => currentStepIndex.value === 0);

  const isAutoplayActive = ref(true);
  const changingSlide = ref(false);
  const intervalId = ref<number>(0);
  const sliderClassAnimation = ref<string>("fade-left");
  const autoplayOptionIndex = ref(0);
  const currentOptionIndex = ref(0);
  let intervalDuration = DEFAULT_INTERVAL_DURATION;

  const selectedFloorplanData = computed(() => {
    const selectedFloorplan = landingFloorplansData.find(
      ({ url }) => url === selectedImages.value.floorplanUrl
    );

    return selectedFloorplan ?? landingFloorplansData[1];
  });

  const selectedImages = ref<{
    facadeUrl: string | null;
    floorplanUrl: string | null;
    optionalStepUrl: string | null;
  }>({
    facadeUrl: builderDemoImagesData.facades[0].url,
    floorplanUrl: builderDemoImagesData.floorplans[0].url,
    optionalStepUrl: builderDemoImagesData.optionalStep[0].url,
  });

  const resetSlider = async () => {
    currentStepIndex.value = 0;
    selectedImages.value = {
      facadeUrl: null,
      floorplanUrl: null,
      optionalStepUrl: null,
    };
    await nextTick(() => mobileScrollToLotInfo);
  };
  const mobileScrollToLotInfo = () => {
    if (isDesktop.value) return;

    scrollToElementIfNotVisible(LOT_INFO_ELEMENT_SELECTOR, FIXED_HEADER_HEIGHT);
  };

  const onClickNextStep = () => changeStep(currentStepIndex.value + 1);

  const onClickPrevStep = () => changeStep(currentStepIndex.value - 1);

  const onClickChangeOption = (optionUrl: string) => {
    stopAutoplay();
    changeOption(optionUrl);
  };
  const changeOption = (option: string) => {
    if (currentStep.value === Steps.SELECT_DESIGN) {
      selectedImages.value.facadeUrl = option;
    }

    if (currentStep.value === Steps.FLOORPLAN) {
      selectedImages.value.floorplanUrl = option;
    }

    if (currentStep.value === Steps.OPTIONAL_STEP)
      selectedImages.value.optionalStepUrl = option;
  };

  const sliderImage = computed(() => {
    const { facadeUrl, floorplanUrl, optionalStepUrl } = selectedImages.value;
    const { facades, floorplans, optionalStep } = builderDemoImagesData;

    if (currentStep.value === Steps.SELECT_DESIGN) {
      return facadeUrl ?? facades[0].url;
    }

    if (currentStep.value === Steps.FLOORPLAN) {
      return floorplanUrl ?? floorplans[0].url;
    }

    return optionalStepUrl ?? optionalStep[0].url;
  });

  const isLastStep = computed(
    () => currentStepIndex.value === sliderSteps.value.length - 1
  );

  const changeStep = (stepNumber: number) => {
    sliderClassAnimation.value =
      stepNumber < currentStepIndex.value ? "fade-right" : "fade-left";
    currentStepIndex.value = stepNumber;
    mobileScrollToLotInfo();
  };

  const changeAutoPlayStep = () => {
    const lastIndex = builderDemoImagesData.facades.length - 1;
    const nextIndex = (autoplayOptionIndex.value + 1) % (lastIndex + 1);
    const { facades } = builderDemoImagesData;

    autoplayOptionIndex.value = nextIndex;

    changeOption(facades[nextIndex].url);
  };

  const startAutoplay = () => {
    clearInterval(intervalId.value);
    isAutoplayActive.value = true;
    intervalId.value = setInterval(
      () => changeAutoPlayStep(),
      intervalDuration
    );
  };
  const stopAutoplay = () => {
    clearInterval(intervalId.value);
    isAutoplayActive.value = false;
  };
  const onTouchStart = (event: TouchEvent) => {
    stopAutoplay();
    touchStartX = event.touches[0].clientX;
  };

  const currentOptions = computed(() => {
    if (currentStep.value === Steps.SELECT_DESIGN) {
      return builderDemoImagesData.facades;
    }

    if (currentStep.value === Steps.FLOORPLAN) {
      return builderDemoImagesData.floorplans;
    }

    return builderDemoImagesData.optionalStep;
  });

  let touchStartX = 0;
  const onTouchEnd = (event: TouchEvent) => {
    const SWIPE_THRESHOLD = 50;
    const touchEndX = event.changedTouches[0].clientX;
    const deltaX = touchEndX - touchStartX;

    if (Math.abs(deltaX) < SWIPE_THRESHOLD) {
      return;
    }

    const options = currentOptions.value;
    const optionsLength = options.length;
    const isSwipeLeft = deltaX < SWIPE_THRESHOLD;

    let newIndex = currentOptionIndex.value;

    if (isSwipeLeft) {
      newIndex = (newIndex + 1) % optionsLength;
    } else {
      newIndex = (newIndex - 1 + optionsLength) % optionsLength;
    }

    currentOptionIndex.value = newIndex;
    changeOption(options[newIndex].url);
  };

  watch(
    currentStepIndex,
    () => {
      stopAutoplay();
      currentOptionIndex.value = 0;
    },
    { deep: true }
  );

  watch(autoplayOptionIndex, async () => {
    changingSlide.value = true;
    await nextTick(() => (changingSlide.value = false));
  });

  onMounted(() => setTimeout(() => startAutoplay(), 2000));
  onBeforeUnmount(() => stopAutoplay());

const __returned__ = { IS_DESKTOP_BREAKPOINT, FIXED_HEADER_HEIGHT, LOT_INFO_ELEMENT_SELECTOR, DEFAULT_INTERVAL_DURATION, isDesktop, sliderSteps, currentStepIndex, currentStep, prevStep, isFirstStep, isAutoplayActive, changingSlide, intervalId, sliderClassAnimation, autoplayOptionIndex, currentOptionIndex, get intervalDuration() { return intervalDuration }, set intervalDuration(v) { intervalDuration = v }, selectedFloorplanData, selectedImages, resetSlider, mobileScrollToLotInfo, onClickNextStep, onClickPrevStep, onClickChangeOption, changeOption, sliderImage, isLastStep, changeStep, changeAutoPlayStep, startAutoplay, stopAutoplay, onTouchStart, currentOptions, get touchStartX() { return touchStartX }, set touchStartX(v) { touchStartX = v }, onTouchEnd, get useIsDesktop() { return useIsDesktop }, get builderDemoImagesData() { return builderDemoImagesData }, get landingFloorplansData() { return landingFloorplansData }, ref, computed, nextTick, onMounted, onBeforeUnmount, watch, get scrollToElementIfNotVisible() { return scrollToElementIfNotVisible }, get Steps() { return Steps }, BuilderDemoMenuSelection }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})